// API Service pour communiquer avec le backend
const API_URL = process.env.REACT_APP_BACKEND_URL;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Don't redirect for login attempts - let the caller handle the error
      if (!endpoint.includes('/auth/login')) {
        this.setToken(null);
        window.location.href = '/login';
      }
      const data = await response.json();
      throw new Error(data.detail || 'Session expirée');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Une erreur est survenue');
    }

    return data;
  }

  // Auth
  async register(email, password, name) {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.access_token);
    return data;
  }

  logout() {
    this.setToken(null);
  }

  // User
  async getProfile() {
    return this.request('/api/users/me');
  }

  async updateName(name) {
    return this.request(`/api/users/me/name?name=${encodeURIComponent(name)}`, {
      method: 'PUT',
    });
  }

  async getFavorites() {
    return this.request('/api/users/favorites');
  }

  async getFavoritesDetails() {
    return this.request('/api/users/favorites/details');
  }

  async addFavorite(itemId, itemType) {
    return this.request('/api/users/favorites', {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, item_type: itemType }),
    });
  }

  async removeFavorite(itemType, itemId) {
    return this.request(`/api/users/favorites/${itemType}/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Content
  async getDhikrs() {
    return this.request('/api/content/dhikrs');
  }

  async getDhikr(slug) {
    return this.request(`/api/content/dhikrs/${slug}`);
  }

  async getMeditationCategories() {
    return this.request('/api/content/meditations/categories');
  }

  async getMeditationSessions(categorySlug) {
    return this.request(`/api/content/meditations/sessions/${categorySlug}`);
  }

  async getMeditationCategoryInfo(slug) {
    return this.request(`/api/content/meditations/category-info/${slug}`);
  }

  // Sharing
  async shareMeditation(sessionId) {
    return this.request(`/api/sharing/share/${sessionId}`, { method: 'POST' });
  }

  async getSharesToday() {
    return this.request('/api/sharing/shares-today');
  }

  async getSharedMeditation(sessionId) {
    return this.request(`/api/sharing/meditation/${sessionId}`);
  }

  async getRespirationExercises() {
    return this.request('/api/content/respirations');
  }

  async getAmbianceSounds() {
    return this.request('/api/content/ambiances');
  }

  // Subscriptions
  async getSubscriptionPlans() {
    return this.request('/api/subscriptions/plans');
  }

  async getSubscriptionStatus() {
    return this.request('/api/subscriptions/status');
  }

  async createCheckout(planId) {
    const originUrl = window.location.origin;
    return this.request('/api/subscriptions/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId, origin_url: originUrl }),
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/api/subscriptions/checkout/status/${sessionId}`);
  }

  async createPortalSession() {
    return this.request('/api/subscriptions/portal', {
      method: 'POST',
      body: JSON.stringify({ origin_url: window.location.origin }),
    });
  }

  async createSupportRequest(payload) {
    return this.request('/api/support/requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getSupportRequests() {
    return this.request('/api/support/requests');
  }

  async updateSupportRequest(requestId, status) {
    return this.request(`/api/support/requests/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getSupportScreenshot(requestId) {
    return this.request(`/api/support/requests/${requestId}/screenshot`);
  }

  async createGiftCheckout(payload, signal) {
    return this.request('/api/gifts/checkout', { method: 'POST', body: JSON.stringify(payload), signal });
  }

  async getGiftCheckoutStatus(sessionId) {
    return this.request(`/api/gifts/checkout/status/${sessionId}`);
  }

  async getMyGifts() {
    return this.request('/api/gifts/mine');
  }

  async resendGift(giftId) {
    return this.request(`/api/gifts/${giftId}/resend`, { method: 'POST' });
  }

  async updateGiftRecipient(giftId, email) {
    return this.request(`/api/gifts/${giftId}/recipient`, { method: 'PATCH', body: JSON.stringify({ recipient_email: email }) });
  }

  async activateGift(token) {
    return this.request('/api/gifts/activate', { method: 'POST', body: JSON.stringify({ token }) });
  }

  async getAdminGifts() {
    return this.request('/api/gifts/admin');
  }

  async cancelSubscription() {
    return this.request('/api/subscriptions/cancel', {
      method: 'POST',
    });
  }

  // Admin
  async getAdminStats() {
    return this.request('/api/admin/stats');
  }

  async getAdminUsers() {
    return this.request('/api/admin/users');
  }

  async getAdminUsersList(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/api/admin/users-list?${qs}`);
  }

  async deleteTestUsers(emails, confirmation) {
    return this.request('/api/admin/users/delete-test', {
      method: 'POST',
      body: JSON.stringify({ emails, confirmation }),
    });
  }

  async grantContributorAccess(email, duration, confirmation) {
    return this.request('/api/admin/users/contributor-access', {
      method: 'POST',
      body: JSON.stringify({ email, duration, confirmation }),
    });
  }

  async extendContributorAccess(email, duration) {
    return this.request('/api/admin/users/contributor-access/extend', {
      method: 'POST',
      body: JSON.stringify({ email, duration, confirm: true }),
    });
  }

  async contributorToLifetime(email) {
    return this.request('/api/admin/users/contributor-access/lifetime', {
      method: 'POST',
      body: JSON.stringify({ email, confirm: true }),
    });
  }

  async revokeContributorAccess(email) {
    return this.request('/api/admin/users/contributor-access/revoke', {
      method: 'POST',
      body: JSON.stringify({ email, confirm: true }),
    });
  }

  async deleteDuplicateUser(email, confirmationEmail) {
    return this.request('/api/admin/users/delete-duplicate', {
      method: 'POST',
      body: JSON.stringify({ email, confirmation_email: confirmationEmail }),
    });
  }

  // Admin - Meditation Categories
  async createMeditationCategory(data) {
    return this.request('/api/admin/meditations/categories', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateMeditationCategory(id, data) {
    return this.request(`/api/admin/meditations/categories/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteMeditationCategory(id) {
    return this.request(`/api/admin/meditations/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin - Meditation Sessions
  async createMeditationSession(data) {
    return this.request('/api/admin/meditations/sessions', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateMeditationSession(id, data) {
    return this.request(`/api/admin/meditations/sessions/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async getAdminMeditationCategories() {
    return this.request('/api/admin/meditations/categories');
  }

  async setMeditationCategoryStatus(id, status) {
    return this.request(`/api/admin/meditations/categories/${id}/status`, {
      method: 'PUT', body: JSON.stringify({ status }),
    });
  }

  async deleteMeditationSession(id) {
    return this.request(`/api/admin/meditations/sessions/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin - Ambiances
  async createAmbiance(data) {
    return this.request('/api/admin/ambiances', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateAmbiance(id, data) {
    return this.request(`/api/admin/ambiances/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteAmbiance(id) {
    return this.request(`/api/admin/ambiances/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin - Audio Upload
  async uploadAudio(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${API_URL}/api/admin/upload-audio`;
    const headers = {};
    if (this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Erreur upload');
    }
    
    return response.json();
  }

  // Admin - Dhikrs
  async createDhikr(data) {
    return this.request('/api/admin/dhikrs', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateDhikr(id, data) {
    return this.request(`/api/admin/dhikrs/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteDhikr(id) {
    return this.request(`/api/admin/dhikrs/${id}`, {
      method: 'DELETE',
    });
  }

  // Humeurs, pratique & bilans mensuels
  async getMoodStatus(date) {
    return this.request(`/api/moods/status?date=${date}`);
  }

  async saveMood(emotion, date) {
    return this.request('/api/moods', {
      method: 'POST', body: JSON.stringify({ emotion, date }),
    });
  }

  async markMoodPromptSeen(date) {
    return this.request('/api/moods/prompt-seen', {
      method: 'POST', body: JSON.stringify({ date }),
    });
  }

  async getMoodRecap(month) {
    return this.request(`/api/moods/recap?month=${month}`);
  }

  async completePractice(payload) {
    return this.request('/api/practice/complete', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  async getPendingBilan(today) {
    return this.request(`/api/bilans/pending?today=${today}`);
  }

  async markBilanSeen(month) {
    return this.request('/api/bilans/seen', {
      method: 'POST', body: JSON.stringify({ month }),
    });
  }

  async getBilans() {
    return this.request('/api/bilans');
  }
}

export const api = new ApiService();
export default api;
